"use client";

import React, {useEffect, useRef, useState} from "react";
import {cn} from "@/lib/utils";
import Image, {StaticImageData} from "next/image";

declare global {
  interface Window {
    UnicornStudio?: {
      isInitialized: boolean;
      init: () => void;
    };
  }
  var UnicornStudio: {
    init: () => void;
  };
}

export default function UnicornEmbedWrapper({
  assetID,
  placeholderImage,
  placeholderCover = false,
  className,
}: {
  assetID: string;
  placeholderImage: StaticImageData;
  placeholderCover?: boolean;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!window.UnicornStudio) {
      window.UnicornStudio = {
        isInitialized: false,
        init: () => {} // Placeholder, will be replaced by the script
      };
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v1.4.35/dist/unicornStudio.umd.js";
      script.onload = function() {
        if (!window.UnicornStudio.isInitialized) {
          UnicornStudio.init();
          window.UnicornStudio.isInitialized = true;
        }
      };
      (document.head || document.body).appendChild(script);
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        });
      },
      {
        root: null,
        rootMargin: "-0px",
        threshold: 0.01,
      }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isVisible && window.UnicornStudio?.isInitialized) {
      // Wait for the DOM to update, then reinitialize
      const timer = setTimeout(() => {
        if (window.UnicornStudio) {
          UnicornStudio.init();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "absolute inset-0 w-full h-full",
        className
      )}
    >
      {(placeholderCover || !isVisible) && (
        <Image
          className="absolute top-0 left-0 !w-full !h-full object-cover"
          src={placeholderImage}
          alt=""
        />
      )}

      {isVisible && (
        <div
          className={cn(
            "w-full h-full",
            placeholderCover && "relative"
          )}
          data-us-project={assetID}
        />
      )}
    </div>
  );
}